// @flow
import {
  takeEvery, call, put, take
} from 'redux-saga/effects';
import jsCookie from 'js-cookie';
import axios from 'axios';
import { toLower } from 'lodash';

import * as httpHeadersService from '@services/httpHeaders';
import { getResource } from '@redux/components/ApiRequest/actions';
import type { NextCtx } from '@globalTypes';

import { INDEX, PROFILE_SUB_TAB, PROFILE } from '@routes';
import { Router } from '@i18n';
import { authApiUrl, mainApiUrl } from '@lib';

import {
  REFRESH_TOKEN_REQUESTING,
  ACCESS_TOKEN_REQUESTING,
  LOGIN_SUCCESS,
  LOGOUT,
  REQUEST_CANDIDATE_ID_SUCCESS,
  REQUEST_CANDIDATE_ID_FAILURE
} from './constants';
import {
  failedLogin,
  successLogin,
  requestCandidateIdSuccess,
  requestCandidateIdFailure
} from './actions';

import type { Action, TokenAPIResponse } from '../types';

export function* refreshTokenRequestFlow(
  nextCtx: NextCtx,
  { email, password }: Action
): Generator<Function, void, void> {
  try {
    const username: ?string = yield call(toLower, email);

    const response: ?TokenAPIResponse = yield call(
      axios.post,
      `${authApiUrl}/v1/token`,
      {
        grant_type: 'password',
        username,
        password
      },
      { headers: httpHeadersService.getHeaders(nextCtx) }
    );

    if (!response) return;

    yield put(successLogin(response.data.refresh_token, response.data.access_token));
  } catch (error) {
    yield put(failedLogin(error?.response?.data));
  }
}

export function* accessTokenRequestFlow(
  nextCtx: NextCtx,
  { refreshToken }: Action
): Generator<Function, void, void> {
  try {
    const response: ?TokenAPIResponse = yield call(
      axios.post,
      `${authApiUrl}/v1/token`,
      {
        grant_type: 'refresh_token',
        refresh_token: refreshToken
      },
      { headers: httpHeadersService.getHeaders(nextCtx) }
    );

    if (!response) return;

    yield put(successLogin(response.data.refresh_token, response.data.access_token));
  } catch (error) {
    yield put(failedLogin(error?.response?.data));
  }
}

export function* loginSuccess({ accessToken }: Action): Generator<Function, void, any> {
  yield call(jsCookie.set, 'accessToken', accessToken || '', { expires: 30 });

  yield put(
    getResource(
      `${mainApiUrl}/v1/patients/any`,
      requestCandidateIdSuccess,
      requestCandidateIdFailure
    )
  );

  const action: Action = yield take([REQUEST_CANDIDATE_ID_SUCCESS, REQUEST_CANDIDATE_ID_FAILURE]);

  if ((action.data?.attributes?.completion_rate || 0) > 30) {
    yield call(Router.pushRoute, PROFILE, {
      primaryTab: 'dashboard'
    });
  } else {
    yield call(Router.pushRoute, PROFILE_SUB_TAB, {
      primaryTab: 'default',
      secondaryTab: 'dashboard'
    });
  }
}

export function logoutFlow(): void {
  jsCookie.remove('accessToken');

  Router.pushRoute(INDEX);
}

export function* signInFlow(nextCtx: NextCtx, action: Action): Generator<Function, void, Action> {
  switch (action.type) {
    case REFRESH_TOKEN_REQUESTING:
      yield call(refreshTokenRequestFlow, nextCtx, action);
      break;
    case ACCESS_TOKEN_REQUESTING:
      yield call(accessTokenRequestFlow, nextCtx, action);
      break;
    case LOGIN_SUCCESS:
      yield call(loginSuccess, action);
      break;
    case LOGOUT:
      yield call(logoutFlow, action);
      break;
    default:
      break;
  }
}

function* signInWatcher(nextCtx: NextCtx): Generator<Function, void, Action> {
  yield takeEvery(
    [REFRESH_TOKEN_REQUESTING, ACCESS_TOKEN_REQUESTING, LOGIN_SUCCESS, LOGOUT],
    signInFlow,
    nextCtx
  );
}

export default signInWatcher;
